import isNil from 'lodash/isNil';

/**
 * The jackpot pot, printed in full with thousands separators ("1,205,000") and never K/M abbreviated.
 *
 * Deliberately NOT `formatAmountByCoinType`, which abbreviates the GC/CC side to "487.15M": the number
 * under the crown is the advert, and the whole figure is the point.
 *
 * ## Two decimals, or none
 * An integer pot prints as an integer; a fractional one prints to exactly 2dp, rounded. A bare
 * `toLocaleString()` — which is what both widgets used to call — follows neither rule: it prints up to
 * THREE fraction digits and drops trailing zeros, so a pot ticking through 487,154,146.5 then
 * 487,154,146.567 rendered as `.5` then `.567`, changing width as it moved.
 *
 * This is game-ui's `formatJackpotAmount` (`src/modules/jackpot/formatJackpotAmount.ts`), reproduced
 * because the two repos share no code. The popup's ladder, the in-slot banner and the lobby widgets are
 * the same pot seen from three surfaces, and they have to agree digit for digit.
 *
 * Anything unparseable passes through untouched rather than rendering as "NaN".
 */
export function formatJackpotAmount(amount: string | number | null | undefined): string {
  if (isNil(amount) || amount === '') {
    return '';
  }

  const value = typeof amount === 'number' ? amount : Number(amount);

  if (!Number.isFinite(value)) {
    return String(amount);
  }

  return Number.isInteger(value)
    ? value.toLocaleString()
    : Number(value.toFixed(2)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** A surface's digit budget, straight from KRA-397's "up to N digits" rules. */
export type JackpotRoundingBudget = {
  /** A figure with up to this many integer digits prints in full. */
  fullUpToDigits: number;
  /** Up to this many digits prints as K; anything longer prints as M. */
  kUpToDigits: number;
};

/**
 * A pot within the caller's digit budget prints in full (via
 * {@link formatJackpotAmount}); past it, the pot rounds to K, then to M — grouped, one decimal at
 * most ("6,000,013.8K"), and M is the top tier no matter how large the figure. Every locale file
 * spells `numbers.thousand`/`numbers.million` as K/M, so the suffixes are literal here.
 *
 * Nil and unparseable input defer to {@link formatJackpotAmount} too. The category row never rounds
 * renders the plain formatter, not this one.
 */
export function formatJackpotAmountRounded(
  amount: string | number | null | undefined,
  { fullUpToDigits, kUpToDigits }: JackpotRoundingBudget,
): string {
  const value = typeof amount === 'number' ? amount : Number(amount);

  if (isNil(amount) || amount === '' || !Number.isFinite(value) || value < 10 ** fullUpToDigits) {
    return formatJackpotAmount(amount);
  }

  const [divisor, suffix] = value < 10 ** kUpToDigits ? ([1_000, 'K'] as const) : ([1_000_000, 'M'] as const);

  return `${(value / divisor).toLocaleString(undefined, { maximumFractionDigits: 1 })}${suffix}`;
}
